import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import type {
  ApiEnvelope,
  IdeListTemplatesResponse,
  IdeListWorkspacesResponse,
  IdeWorkspace,
  IdeWorkspaceResponse,
  IdeWorkspaceTemplate,
} from '@etherworks/shared-types';
import {
  AlertTriangle,
  CheckCircle2,
  Clock3,
  Code2,
  Loader2,
  Play,
  Plus,
  RefreshCw,
  Square,
  Trash2,
} from 'lucide-react';
import PageTitle from '../components/PageTitle';
import { WorkspaceShell } from '../components/layout';
import { getApiErrorMessage } from '../utils/errorMessages';

type StatusTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger';

const statusTone: Record<IdeWorkspace['status'], StatusTone> = {
  creating: 'info',
  starting: 'info',
  running: 'success',
  stopping: 'warning',
  stopped: 'neutral',
  failed: 'danger',
  deleting: 'warning',
  deleted: 'neutral',
};

const toneClasses: Record<StatusTone, string> = {
  neutral: 'bg-muted text-muted-foreground',
  info: 'bg-sky-500/10 text-sky-600 dark:text-sky-400',
  success: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  warning: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  danger: 'bg-destructive/10 text-destructive',
};

const transientStatuses: IdeWorkspace['status'][] = ['creating', 'starting', 'stopping'];

function statusIcon(status: IdeWorkspace['status']) {
  if (status === 'running') return <CheckCircle2 className="h-3.5 w-3.5" />;
  if (status === 'failed') return <AlertTriangle className="h-3.5 w-3.5" />;
  if (status === 'creating' || status === 'starting' || status === 'stopping' || status === 'deleting') return <Loader2 className="h-3.5 w-3.5 animate-spin" />;
  return <Clock3 className="h-3.5 w-3.5" />;
}

function resolveBrowserUrl(url: string) {
  return new URL(url, window.location.origin).toString();
}

export default function IDE() {
  const { t } = useTranslation(['ide', 'common']);
  const navigate = useNavigate();
  const { workspaceId } = useParams<{ workspaceId?: string }>();
  const [templates, setTemplates] = useState<IdeWorkspaceTemplate[]>([]);
  const [workspaces, setWorkspaces] = useState<IdeWorkspace[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [workspaceName, setWorkspaceName] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [actionId, setActionId] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [launchMessage, setLaunchMessage] = useState(t('launch_loading'));
  const [launchError, setLaunchError] = useState<string | null>(null);
  const [launchRetryKey, setLaunchRetryKey] = useState(0);

  const sortedWorkspaces = useMemo(
    () => [...workspaces].sort((left, right) => new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime()),
    [workspaces],
  );

  const updateWorkspace = (workspace: IdeWorkspace) => {
    setWorkspaces(previous => previous.some(item => item.id === workspace.id)
      ? previous.map(item => item.id === workspace.id ? workspace : item)
      : [workspace, ...previous]);
  };

  const loadWorkspace = async (targetWorkspaceId: string) => {
    const response = await axios.get<ApiEnvelope<IdeWorkspaceResponse>>(`/api/ide/workspaces/${targetWorkspaceId}`);
    if (response.data.code !== 0 || !response.data.data) throw new Error(response.data.msg || response.data.error || t('load_error'));
    updateWorkspace(response.data.data.workspace);
    return response.data.data.workspace;
  };

  const loadWorkspaces = async () => {
    const response = await axios.get<ApiEnvelope<IdeListWorkspacesResponse>>('/api/ide/workspaces');
    if (response.data.code !== 0 || !response.data.data) throw new Error(response.data.msg || response.data.error || t('load_error'));
    setWorkspaces(response.data.data.workspaces);
  };

  const loadTemplates = async () => {
    const response = await axios.get<ApiEnvelope<IdeListTemplatesResponse>>('/api/ide/templates');
    if (response.data.code !== 0 || !response.data.data) throw new Error(response.data.msg || response.data.error || t('load_error'));
    setTemplates(response.data.data.templates);
    if (!selectedTemplateId && response.data.data.templates.length > 0) {
      setSelectedTemplateId(response.data.data.templates[0].id);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([loadTemplates(), loadWorkspaces()]);
    } catch (error) {
      toast.error(getApiErrorMessage(error as any, t, 'ide:load_error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!workspaceId) void loadData();
  }, [workspaceId]);

  useEffect(() => {
    if (!workspaceId) return;

    let cancelled = false;
    let retryTimer: number | undefined;

    const scheduleRetry = () => {
      retryTimer = window.setTimeout(() => {
        if (!cancelled) setLaunchRetryKey(value => value + 1);
      }, 2000);
    };

    const launchWorkspace = async () => {
      setLaunchError(null);
      setLaunchMessage(t('launch_loading'));
      try {
        let workspace = await loadWorkspace(workspaceId);
        if (cancelled) return;

        if (workspace.status === 'running' && workspace.ideUrl) {
          window.location.replace(resolveBrowserUrl(workspace.ideUrl));
          return;
        }

        if (workspace.status === 'deleted' || workspace.status === 'deleting') {
          throw new Error(t('workspace_unavailable'));
        }

        if (workspace.status === 'stopped' || workspace.status === 'failed') {
          setActionId(`${workspace.id}:start`);
          setLaunchMessage(t('launch_starting'));
          const response = await axios.post<ApiEnvelope<IdeWorkspaceResponse>>(`/api/ide/workspaces/${workspace.id}/start`);
          if (response.data.code !== 0 || !response.data.data) throw response.data;
          workspace = response.data.data.workspace;
          updateWorkspace(workspace);
          if (cancelled) return;
          setActionId(null);
        }

        if (workspace.status === 'running' && workspace.ideUrl) {
          window.location.replace(resolveBrowserUrl(workspace.ideUrl));
          return;
        }

        if (transientStatuses.includes(workspace.status)) {
          setLaunchMessage(t('launch_waiting'));
          scheduleRetry();
          return;
        }

        throw new Error(t('open_error'));
      } catch (error) {
        if (!cancelled) {
          setActionId(null);
          setLaunchError(getApiErrorMessage(error as any, t, 'ide:open_error'));
        }
      }
    };

    void launchWorkspace();

    return () => {
      cancelled = true;
      if (retryTimer) window.clearTimeout(retryTimer);
    };
  }, [workspaceId, launchRetryKey, t]);

  const createWorkspace = async () => {
    if (!selectedTemplateId || !workspaceName.trim()) {
      toast.error(t('create_required'));
      return;
    }
    setCreating(true);
    try {
      const response = await axios.post<ApiEnvelope<IdeWorkspaceResponse>>('/api/ide/workspaces', {
        templateId: selectedTemplateId,
        name: workspaceName.trim(),
      });
      if (response.data.code !== 0 || !response.data.data) throw response.data;
      setWorkspaces(previous => [response.data.data!.workspace, ...previous]);
      setWorkspaceName('');
      setShowCreateForm(false);
      toast.success(t('created'));
      navigate(`/code/${response.data.data.workspace.id}`);
    } catch (error) {
      toast.error(getApiErrorMessage(error as any, t, 'ide:create_error'));
    } finally {
      setCreating(false);
    }
  };

  const workspaceAction = async (workspace: IdeWorkspace, action: 'start' | 'stop' | 'delete') => {
    setActionId(`${workspace.id}:${action}`);
    try {
      const request = action === 'delete'
        ? axios.delete<ApiEnvelope<IdeWorkspaceResponse>>(`/api/ide/workspaces/${workspace.id}`)
        : axios.post<ApiEnvelope<IdeWorkspaceResponse>>(`/api/ide/workspaces/${workspace.id}/${action}`);
      const response = await request;
      if (response.data.code !== 0 || !response.data.data) throw response.data;
      const nextWorkspace = response.data.data.workspace;
      setWorkspaces(previous => action === 'delete'
        ? previous.filter(item => item.id !== workspace.id)
        : previous.map(item => item.id === workspace.id ? nextWorkspace : item));
      toast.success(t(`${action}_success`));
    } catch (error) {
      toast.error(getApiErrorMessage(error as any, t, `ide:${action}_error`));
    } finally {
      setActionId(null);
    }
  };

  if (workspaceId) {
    return (
      <div className="h-screen w-screen bg-background text-foreground flex items-center justify-center p-6">
        <PageTitle titleKey="title" descriptionKey="subtitle" namespace="ide" />
        <div className="w-full max-w-sm text-center">
          <div className="mx-auto h-12 w-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
            {launchError ? <AlertTriangle className="h-6 w-6" /> : <Loader2 className="h-6 w-6 animate-spin" />}
          </div>
          <h1 className="mt-4 text-lg font-semibold tracking-tight">{launchError ? t('launch_error') : t('launch_title')}</h1>
          <p className="mt-2 text-sm text-muted-foreground">{launchError || launchMessage}</p>
          {launchError && (
            <div className="mt-5 flex items-center justify-center gap-2">
              <button
                type="button"
                onClick={() => setLaunchRetryKey(value => value + 1)}
                className="rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 inline-flex items-center gap-2 transition-colors"
              >
                <RefreshCw className="h-4 w-4" />
                {t('common:retry')}
              </button>
              <button
                type="button"
                onClick={() => navigate('/code')}
                className="rounded-lg border border-border bg-background px-3 py-2 text-sm font-medium text-foreground hover:bg-accent transition-colors"
              >
                {t('common:back')}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <WorkspaceShell
      title={t('title')}
      subtitle={t('subtitle')}
      actions={(
        <>
          <button
            type="button"
            onClick={loadData}
            disabled={loading}
            className="h-9 w-9 rounded-lg border border-border bg-card text-muted-foreground hover:text-foreground hover:bg-accent inline-flex items-center justify-center transition-colors disabled:opacity-60"
            title={t('common:refresh')}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            type="button"
            onClick={() => setShowCreateForm(value => !value)}
            className="rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 inline-flex items-center gap-2 transition-colors"
          >
            <Plus className="h-4 w-4" />
            {t('new_dev_space')}
          </button>
        </>
      )}
    >
      <PageTitle titleKey="title" descriptionKey="subtitle" namespace="ide" />
      <div className="flex w-full flex-col gap-6">

          {showCreateForm && (
            <section className="rounded-lg border border-border bg-card p-4">
              <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] md:items-center">
                <select
                  value={selectedTemplateId}
                  onChange={event => setSelectedTemplateId(event.target.value)}
                  className="h-10 rounded-lg border border-border bg-background px-3 text-sm text-foreground outline-none focus:ring-2 focus:ring-primary/30"
                >
                  {templates.map(template => (
                    <option key={template.id} value={template.id}>{template.name}</option>
                  ))}
                </select>
                <input
                  value={workspaceName}
                  onChange={event => setWorkspaceName(event.target.value)}
                  placeholder={t('workspace_name_placeholder')}
                  maxLength={80}
                  className="h-10 rounded-lg border border-border bg-background px-3 text-sm text-foreground outline-none focus:ring-2 focus:ring-primary/30"
                />
                <button
                  type="button"
                  onClick={createWorkspace}
                  disabled={creating || templates.length === 0}
                  className="h-10 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90 inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
                >
                  {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                  {t('create')}
                </button>
              </div>
            </section>
          )}

          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-medium text-foreground">{t('workspaces')}</h2>
              <span className="text-sm text-muted-foreground">{workspaces.length}</span>
            </div>

            {loading && workspaces.length === 0 ? (
              <div className="rounded-lg border border-border bg-card p-5 flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('common:loading')}
              </div>
            ) : sortedWorkspaces.length === 0 ? (
              <div className="rounded-lg border border-dashed border-border bg-card p-8 text-center">
                <div className="mx-auto h-12 w-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                  <Code2 className="h-6 w-6" />
                </div>
                <h3 className="mt-4 text-base font-semibold text-foreground">{t('empty_title')}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{t('empty_subtitle')}</p>
              </div>
            ) : (
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {sortedWorkspaces.map(workspace => (
                  <article key={workspace.id} className="rounded-lg border border-border bg-card p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="truncate text-sm font-medium text-foreground">{workspace.name}</h3>
                        <p className="mt-1 truncate text-xs text-muted-foreground">{workspace.templateId}</p>
                      </div>
                      <span className={`inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium ${toneClasses[statusTone[workspace.status]]}`}>
                        {statusIcon(workspace.status)}
                        {t(`status.${workspace.status}`)}
                      </span>
                    </div>
                    <div className="mt-4 flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => navigate(`/code/${workspace.id}`)}
                        className="rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 inline-flex items-center gap-2 transition-colors"
                      >
                        <Play className="h-4 w-4" />
                        {workspace.status === 'running' ? t('open_vscode') : t('start_and_open_vscode')}
                      </button>
                      {workspace.status === 'running' && (
                        <button
                          type="button"
                          onClick={() => workspaceAction(workspace, 'stop')}
                          disabled={actionId === `${workspace.id}:stop`}
                          className="rounded-lg border border-border bg-background px-3 py-2 text-sm font-medium text-foreground hover:bg-accent inline-flex items-center gap-2 transition-colors disabled:opacity-60"
                        >
                          {actionId === `${workspace.id}:stop` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Square className="h-4 w-4" />}
                          {t('stop')}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => workspaceAction(workspace, 'delete')}
                        disabled={actionId === `${workspace.id}:delete`}
                        className="h-9 w-9 rounded-lg border border-destructive/30 bg-destructive/5 text-destructive hover:bg-destructive/10 inline-flex items-center justify-center transition-colors disabled:opacity-60"
                        title={t('common:delete')}
                      >
                        {actionId === `${workspace.id}:delete` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
      </div>
    </WorkspaceShell>
  );
}